import apiService, { get } from './apiService';

const API_BASE_URL = '/api/performance';

export const performanceService = {
  // Project methods - read-only use get(), write operations use auth methods
  getProjects: async () => {
    return await get(`${API_BASE_URL}/projects`);
  },

  getProject: async (projectId) => {
    return await get(`${API_BASE_URL}/projects/${projectId}`);
  },

  createProject: async (projectData) => {
    return await apiService.authPost(`${API_BASE_URL}/projects`, projectData);
  },

  updateProject: async (projectId, projectData) => {
    return await apiService.authPut(`${API_BASE_URL}/projects/${projectId}`, projectData);
  },

  deleteProject: async (projectId) => {
    return await apiService.authDelete(`${API_BASE_URL}/projects/${projectId}`);
  },

  // Execution methods - read-only use get(), write operations use auth methods
  getExecutions: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${API_BASE_URL}/executions?${queryString}` : `${API_BASE_URL}/executions`;
    return await get(url);
  },

  getExecution: async (executionId) => {
    return await get(`${API_BASE_URL}/executions/${executionId}`);
  },

  deleteExecution: async (executionId) => {
    return await apiService.authDelete(`${API_BASE_URL}/executions/${executionId}`);
  },

  createExecution: async (formData) => {
    // For file uploads, we need to use fetch directly since authPost expects JSON
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    
    // Use the same URL construction logic as apiService
    const getCurrentBaseUrl = async () => {
      if (process.env.NODE_ENV === 'development') {
        return '';
      }
      
      if (window.RUNTIME_CONFIG && window.RUNTIME_CONFIG.API_URL) {
        return window.RUNTIME_CONFIG.API_URL;
      }
      return 'http://localhost:5001';
    };
    
    const baseUrl = await getCurrentBaseUrl();
    const url = `${API_BASE_URL}/executions`;
    const fullUrl = url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Request failed with status: ${response.status}`);
    }

    return await response.json();
  },

  getExecutionSummary: async (executionId) => {
    return await get(`${API_BASE_URL}/executions/${executionId}/summary`);
  },

  getExecutionLabelStats: async (executionId) => {
    return await get(`${API_BASE_URL}/executions/${executionId}/label-stats`);
  },

  // Performance Stats methods (new) - read-only
  getExecutionStats: async (executionId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString 
      ? `${API_BASE_URL}/stats/execution/${executionId}?${queryString}` 
      : `${API_BASE_URL}/stats/execution/${executionId}`;
    return await get(url);
  },

  // Results methods (legacy support) - read-only
  getExecutionResults: async (executionId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString 
      ? `${API_BASE_URL}/results/execution/${executionId}?${queryString}` 
      : `${API_BASE_URL}/results/execution/${executionId}`;
    return await get(url);
  },

  // Analytics methods (for future use) - read-only
  getProjectAnalytics: async (projectId) => {
    return await get(`${API_BASE_URL}/analytics/project/${projectId}`);
  },

  getPerformanceTrends: async () => {
    return await get(`${API_BASE_URL}/analytics/trends`);
  },

  // Trends methods - read-only
  getTrendsData: async (filters) => {
    // Handle multiple actions by sending them as separate parameters
    const params = new URLSearchParams();
    
    if (filters.projectId) params.append('projectId', filters.projectId);
    if (filters.userLoad) params.append('userLoad', filters.userLoad);
    
    // Handle actions array
    if (filters.actions && Array.isArray(filters.actions)) {
      filters.actions.forEach(action => {
        params.append('actions', action);
      });
    } else if (filters.action) {
      // Backward compatibility for single action
      params.append('actions', filters.action);
    }
    
    const url = params.toString() ? `${API_BASE_URL}/trends?${params.toString()}` : `${API_BASE_URL}/trends`;
    return await get(url);
  },

  getProjectActions: async (projectId) => {
    return await get(`${API_BASE_URL}/projects/${projectId}/actions`);
  },

  getProjectUserLoads: async (projectId, action = null) => {
    const params = action ? { action } : {};
    const queryString = new URLSearchParams(params).toString();
    const url = queryString 
      ? `${API_BASE_URL}/projects/${projectId}/user-loads?${queryString}`
      : `${API_BASE_URL}/projects/${projectId}/user-loads`;
    return await get(url);
  }
};

export default performanceService;
